// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// Semantic find-similar hook (T-304). Calls the backend /similar endpoint, which
// reuses a record's stored embedding and runs match_embeddings under the caller's
// RLS. Used by "similar SOPs / grows / strains" entry points.

import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

export interface SimilarNeighbor {
  source_id: string;
  similarity: number;
  content: string;
  /** Structured display fields when the backend enriches the neighbor. */
  title?: string | null;
  lineage?: string | null;
  breeder?: string | null;
  subtitle?: string | null;
  reasons?: string[];
}

interface SimilarState {
  loading: boolean;
  available: boolean | null; // null = not fetched; false = source not embedded yet
  neighbors: SimilarNeighbor[];
  error: string | null;
}

export type SimilarSourceTable = "sops" | "aurum_sops" | "strains";

export function useSimilar() {
  const [state, setState] = useState<SimilarState>({
    loading: false,
    available: null,
    neighbors: [],
    error: null,
  });

  const fetchSimilar = useCallback(
    async (sourceTable: SimilarSourceTable, sourceId: string, k = 5) => {
      setState({ loading: true, available: null, neighbors: [], error: null });
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error("You must be logged in.");

        const resp = await fetch(`${API_BASE_URL}/similar`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ source_table: sourceTable, source_id: sourceId, k }),
        });
        if (!resp.ok) {
          const detail = await resp.text().catch(() => "");
          throw new Error(detail || `Request failed (HTTP ${resp.status})`);
        }
        const data = await resp.json();
        setState({
          loading: false,
          available: Boolean(data.available),
          neighbors: data.neighbors || [],
          error: null,
        });
      } catch (err) {
        setState({
          loading: false,
          available: null,
          neighbors: [],
          error: err instanceof Error ? err.message : "Unknown error",
        });
      }
    },
    []
  );

  return { ...state, fetchSimilar };
}
